// Listing filters island (collections, search): applies filters, sort and paging without a
// full page load by fetching the server-rendered page and swapping the listing. Without
// JavaScript the filter panel is a plain GET form and every chip and page link is a link.

const PRICE = ["price_min", "price_max"];
const FOCUSABLE = "button, [href], input, select, summary";

// Form values -> canonical URL (repeated keys, `price=min-max`), like the server builds them.
function formUrl(form) {
	const params = new URLSearchParams();
	for (const [key, value] of new FormData(form)) {
		if (value && !PRICE.includes(key)) params.append(key, value);
	}
	const min = form.elements.price_min ? form.elements.price_min.value.trim() : "";
	const max = form.elements.price_max ? form.elements.price_max.value.trim() : "";
	if (min || max) params.set("price", `${min}-${max}`);
	if (params.get("sort") === (form.dataset.defaultSort || "newest")) params.delete("sort");
	const query = params.toString();
	return form.action.split("?")[0] + (query ? `?${query}` : "");
}

export function initFilters(root) {
	let request = 0;
	const html = document.documentElement;
	html.classList.add("has-js");

	// one live region outside the swapped markup, so new result counts are announced
	const status = document.createElement("p");
	status.className = "visually-hidden";
	status.setAttribute("role", "status");
	root.after(status);

	const panel = () => root.querySelector("[data-filters-panel]");
	const isOpen = () => html.classList.contains("filters-open");
	// apply the drawer state to the (possibly new) markup without moving focus
	const sync = () => {
		const open = isOpen();
		const p = panel();
		if (p) {
			p.classList.toggle("is-open", open);
			if (open) {
				p.setAttribute("role", "dialog");
				p.setAttribute("aria-modal", "true");
			} else {
				p.removeAttribute("role");
				p.removeAttribute("aria-modal");
			}
		}
		root.querySelectorAll("[data-filters-toggle][aria-expanded]").forEach((b) =>
			b.setAttribute("aria-expanded", String(open))
		);
		root.querySelectorAll("[data-filters-toggle]").forEach((b) => {
			b.hidden = false;
			b.removeAttribute("data-pending");
		});
	};
	const setOpen = (open, moveFocus) => {
		html.classList.toggle("filters-open", open);
		sync();
		if (!moveFocus) return;
		const target = open
			? panel() && panel().querySelector(".filters__close")
			: root.querySelector(".main-collection__filter-toggle");
		if (target && target.offsetParent) target.focus();
	};
	const focusResults = () => {
		const heading = root.querySelector("h1");
		if (!heading) return;
		heading.tabIndex = -1;
		heading.focus({ preventScroll: true });
	};

	const load = async (url, push, focusSelector) => {
		const id = ++request;
		root.setAttribute("aria-busy", "true");
		try {
			const response = await fetch(url, { headers: { Accept: "text/html" } });
			const doc = new DOMParser().parseFromString(await response.text(), "text/html");
			const fresh = doc.querySelector("[data-collection]");
			if (id !== request) return;
			if (!response.ok || !fresh) return (window.location.href = url);
			// same-origin page rendered by our autoescaping templates: the markup a full load shows
			root.innerHTML = fresh.innerHTML;
			document.title = doc.title;
			if (push) history.pushState({ filters: true }, "", url);
			sync(); // the drawer may have been closed while the request ran
			document.dispatchEvent(new CustomEvent("storefront:updated", { detail: { root } }));
			const count = root.querySelector(".main-collection__count");
			status.textContent = count ? count.textContent : "";
			const target = focusSelector && root.querySelector(focusSelector);
			if (target) target.focus();
			else if (isOpen()) setOpen(true, true);
			else focusResults();
		} catch (e) {
			window.location.href = url;
		} finally {
			if (id === request) root.setAttribute("aria-busy", "false");
		}
	};

	root.addEventListener("change", (event) => {
		const form = root.querySelector("[data-filter-form]");
		const t = event.target;
		// price fields apply with Enter or the Apply button, not while typing
		if (!form || t.form !== form || PRICE.includes(t.name)) return;
		const selector = t.id
			? `#${CSS.escape(t.id)}`
			: `[name="${CSS.escape(t.name)}"][value="${CSS.escape(t.value)}"]`;
		load(formUrl(form), true, selector);
	});
	root.addEventListener("submit", (event) => {
		if (!event.target.matches("[data-filter-form]")) return;
		event.preventDefault();
		setOpen(false, false);
		load(formUrl(event.target), true);
	});
	root.addEventListener("click", (event) => {
		const toggle = event.target.closest("[data-filters-toggle]");
		if (toggle) return setOpen(!isOpen(), true);
		const link = event.target.closest("a[data-filter-link]");
		if (!link || event.metaKey || event.ctrlKey || event.shiftKey) return;
		event.preventDefault();
		load(link.href, true).then(() => root.scrollIntoView({ block: "start" }));
	});
	document.addEventListener("keydown", (event) => {
		if (!isOpen()) return;
		if (event.key === "Escape") return setOpen(false, true);
		if (event.key !== "Tab" || !panel()) return;
		// keep focus inside the open drawer
		const items = [...panel().querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent);
		if (!items.length) return;
		const first = items[0];
		const last = items[items.length - 1];
		if (event.shiftKey && document.activeElement === first) {
			event.preventDefault();
			last.focus();
		} else if (!event.shiftKey && document.activeElement === last) {
			event.preventDefault();
			first.focus();
		}
	});
	window.addEventListener("popstate", () => load(window.location.href, false));
	sync();
}
